const drawerEvents = {
    OnLineItemUpdate: 'action:cart:updateLineItem',
    onQuantityChange: 'on:QuantityChange',
    triggerCartChange: 'action:cart:updateCart',
};

/*
 * Third-party cart sync.
 *
 * 3rd-party apps (Essential Upsell, etc.) hit /cart/add.js, /cart/change.js, etc.
 * directly — they don't dispatch the theme's `triggerCartChange` event, so the
 * drawer stays stale until the page is reloaded. Intercept those requests and,
 * once they finish, refetch cart sections and dispatch the theme event so the
 * drawer (and bubble) re-render with the new line items.
 */
(function setupThirdPartyCartSync() {
    if (typeof window === 'undefined') return;
    if (window.__themeCartFetchPatched) return;
    window.__themeCartFetchPatched = true;

    const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)\.js(\?|$)/;
    const SYNC_DEBOUNCE_MS = 150;
    let pendingSyncTimer = null;
    let inflightSync = false;
    let resyncQueued = false;

    function getDrawerCart() {
        return document.querySelector(
            'drawer-cart[render-section="cart-drawer"]'
        );
    }

    function syncCartDrawer() {
        if (inflightSync) {
            // A mutation arrived while we were syncing — make sure we run
            // another sync after the current one finishes so the drawer never
            // ends up stuck on stale data.
            resyncQueued = true;
            return;
        }
        const drawer = getDrawerCart();
        if (!drawer) return;
        if (
            typeof HELPER_UTIL === 'undefined' ||
            !HELPER_UTIL.sectionRenderUtil ||
            !HELPER_UTIL.dispatchCustomEvent
        ) {
            return;
        }

        inflightSync = true;
        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            'cart-drawer'
        ).getSectionsString();

        // Cache-buster: third-party apps occasionally cache /cart.js responses,
        // which makes us render the pre-mutation snapshot and the count stays
        // stale until a hard reload.
        fetch(
            `${window.Shopify.routes.root}cart.js?sections=${sectionsToRender}&_=${Date.now()}`,
            {
                headers: { Accept: 'application/json' },
                cache: 'no-store',
                credentials: 'same-origin',
            }
        )
            .then((res) => (res && res.ok ? res.json() : null))
            .then((data) => {
                if (!data || !data.sections) return;
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    document,
                    { sections: data.sections }
                );
            })
            .catch(() => {})
            .finally(() => {
                inflightSync = false;
                if (resyncQueued) {
                    resyncQueued = false;
                    scheduleSync();
                }
            });
    }

    function scheduleSync() {
        if (pendingSyncTimer) {
            window.clearTimeout(pendingSyncTimer);
        }
        pendingSyncTimer = window.setTimeout(() => {
            pendingSyncTimer = null;
            syncCartDrawer();
        }, SYNC_DEBOUNCE_MS);
    }

    // Public hook so other code paths (drawer open, bundle add, +/- in drawer)
    // can demand an immediate refresh without waiting on the debounce.
    window.__themeCartDrawerSync = function forceCartDrawerSync() {
        if (pendingSyncTimer) {
            window.clearTimeout(pendingSyncTimer);
            pendingSyncTimer = null;
        }
        syncCartDrawer();
    };

    function urlFromArgs(args) {
        const first = args[0];
        if (typeof first === 'string') return first;
        if (first && typeof first.url === 'string') return first.url;
        return '';
    }

    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
        window.fetch = function patchedFetch(...args) {
            const url = urlFromArgs(args);
            const isCartMutation = CART_MUTATION_RE.test(url);
            const result = origFetch.apply(this, args);
            if (isCartMutation) {
                result
                    .then((res) => {
                        if (res && res.ok) scheduleSync();
                    })
                    .catch(() => {});
            }
            return result;
        };
    }

    if (typeof XMLHttpRequest !== 'undefined') {
        const origOpen = XMLHttpRequest.prototype.open;
        const origSend = XMLHttpRequest.prototype.send;
        XMLHttpRequest.prototype.open = function patchedOpen(
            method,
            url,
            ...rest
        ) {
            this.__themeCartUrl = typeof url === 'string' ? url : '';
            return origOpen.call(this, method, url, ...rest);
        };
        XMLHttpRequest.prototype.send = function patchedSend(...args) {
            if (
                this.__themeCartUrl &&
                CART_MUTATION_RE.test(this.__themeCartUrl)
            ) {
                this.addEventListener('load', () => {
                    if (this.status >= 200 && this.status < 300) {
                        scheduleSync();
                    }
                });
            }
            return origSend.apply(this, args);
        };
    }
})();

class DrawerCart extends HTMLElement {
    constructor() {
        super();
        this.currentLineItem;
        this.lineItemError = null;
        this._thirdPartyRefreshTimers = [];
        this.cartIdentifier = this.getAttribute('render-section');
        this.checkoutContainer = document.querySelector(
            '[cart-checkout-container]'
        );

        this.sectionId =
            this.getAttribute('data-sectionId') || this.cartIdentifier;

            // added by Johannes
            this._lastCartItemCount = Number(this.dataset.cartitemcount || 0);
            this.handleLineItemBound = this.handleLineItem.bind(this);
            this.renderCartBound = this.renderCart.bind(this);
            this.fetchAndRenderCartItemsBound =
                this.fetchAndRenderCartItems.bind(this);
            this.handleCartDrawerShownBound =
                this.handleCartDrawerShown.bind(this);


        this.addEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.handleLineItemBound
        );

        /* Listening for this event to update cart items and show cart drawer base on products Added to cart via product-form,product-card component 
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.triggerCartChange, { detail: {}}));
            Note: Make sure to pass the `detail` @object with the data consisting of sections that can fetched via section rendering Api functionality of Shopify
        */

        document.addEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.renderCartBound
        );

        /* Listening for this event to update cart items and show cart drawer based on custom functionality of the theme 
            Scenarios: When user adds a product to cart, the cart drawer should be updated and shown
            Usage: document.dispatchEvent(new CustomEvent(_EVENT_HELPER.updateAndShowCart, { detail: {}}));
        */
        document.addEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.fetchAndRenderCartItemsBound
        );

        document.addEventListener(
            _EVENT_HELPER.showDrawer,
            this.handleCartDrawerShownBound
        );
    }

    connectedCallback() {
        if (this.cartIdentifier === 'cart-drawer') {
            this.queueThirdPartyCartAppsRefresh({
                reason: 'drawer-connected',
            });
        }
    }

    handleCartDrawerShown(e) {
        if (e?.detail?.drawerType !== 'cart-drawer') return;

        // Intentionally NOT triggering a fresh fetch or third-party app
        // refresh here. The drawer-cart DOM is already populated server-side
        // on page load, the patched fetch in cart-mutation-interceptor.liquid
        // catches every cart mutation, and the lightweight cart-count poll
        // covers any leak. Re-firing the third-party app init on each
        // drawer open caused Essential Upsell cards to visibly redraw twice
        // every time the drawer was reopened.
    }

    clearThirdPartyCartAppRefreshQueue() {
        this._thirdPartyRefreshTimers.forEach((timerId) => {
            window.clearTimeout(timerId);
        });
        this._thirdPartyRefreshTimers = [];
    }

    dispatchThirdPartyCartAppEvent(target, name, detail) {
        try {
            target.dispatchEvent(
                new CustomEvent(name, {
                    detail,
                    bubbles: true,
                })
            );
        } catch (_) {}
    }

    getEssentialBannersSideCartTarget() {
        const bannerConfigs = Array.isArray(window.essentialBannersConfigs)
            ? window.essentialBannersConfigs
            : [];
        const cartBannerConfig = bannerConfigs.find(
            (banner) => banner?.placement?.type === 'cart-page'
        );
        const preferredPlacement =
            cartBannerConfig?.placement?.cartPlacement === 'top'
                ? 'top'
                : 'bottom';

        if (preferredPlacement === 'top') {
            return (
                document.querySelector('.essential-banners-block-side-cart-top') ||
                document.querySelector(
                    '.essential-banners-block-side-cart-bottom'
                )
            );
        }

        return (
            document.querySelector('.essential-banners-block-side-cart-bottom') ||
            document.querySelector('.essential-banners-block-side-cart-top')
        );
    }

    syncEssentialBannersSideCart() {
        if (this.cartIdentifier !== 'cart-drawer') return;

        const target = this.getEssentialBannersSideCartTarget();
        if (!target) return;

        const itemCount = Number(this.dataset.cartitemcount || 0);
        if (itemCount <= 0) {
            target
                .querySelectorAll('[data-essential-banners-fallback="true"]')
                .forEach((banner) => banner.remove());
            return;
        }

        const sourceBanners = document.querySelectorAll(
            '[data-banners] [data-type="cart-page"]'
        );

        if (!sourceBanners.length) return;

        for (let index = sourceBanners.length - 1; index >= 0; index -= 1) {
            const sourceBanner = sourceBanners[index];
            const bannerId = sourceBanner.getAttribute('data-id');

            if (!bannerId) continue;

            if (
                document.querySelector(
                    `.essential-side-cart-banner[data-id="${bannerId}"]`
                )
            ) {
                continue;
            }

            const clonedBanner = sourceBanner.cloneNode(true);

            clonedBanner.style.display = 'flex';
            clonedBanner.classList.add('essential-side-cart-banner');
            clonedBanner.setAttribute('data-essential-banners-fallback', 'true');

            if (
                target.firstElementChild &&
                target.firstElementChild.matches('div[data-type="cart-page"]')
            ) {
                target.firstElementChild.insertAdjacentElement(
                    'beforebegin',
                    clonedBanner
                );
            } else {
                target.append(clonedBanner);
            }
        }
    }

    notifyThirdPartyCartApps(detail = {}) {
        if (this.cartIdentifier !== 'cart-drawer') return;

        // Calling the upsell app's init() repeatedly causes it to stack
        // duplicate click handlers on its add buttons (one click → multiple
        // cart/add.js requests). Throttle so reinit fires at most once per
        // ~1.2s regardless of how many cart events overlap.
        const now = Date.now();
        if (
            window.__essentialReinitAt &&
            now - window.__essentialReinitAt < 1200
        ) {
            return;
        }
        window.__essentialReinitAt = now;

        const payload = {
            ...detail,
            source: 'cart-drawer',
            itemCount: Number(this.dataset.cartitemcount || 0),
        };

        [
            'cart:updated',
            'cart:refresh',
            'cart.requestComplete',
            'essential-upsell:refresh',
            'essentialupsell:cart-changed',
        ].forEach((eventName) => {
            this.dispatchThirdPartyCartAppEvent(document, eventName, payload);
        });

        ['cart:updated', 'essentialupsell:cart-changed'].forEach(
            (eventName) => {
                this.dispatchThirdPartyCartAppEvent(window, eventName, payload);
            }
        );

        if (
            window.EssentialUpsell &&
            typeof window.EssentialUpsell.init === 'function'
        ) {
            window.EssentialUpsell.init();
        }

        if (typeof window.__essential_upsell_refresh === 'function') {
            window.__essential_upsell_refresh();
        }

        if (
            window.EssentialTrustBadges &&
            typeof window.EssentialTrustBadges.init === 'function'
        ) {
            window.EssentialTrustBadges.init();
        }

        if (typeof window.__essential_trust_badges_refresh === 'function') {
            window.__essential_trust_badges_refresh();
        }

        this.syncEssentialBannersSideCart();
    }

    queueThirdPartyCartAppsRefresh(detail = {}) {
        if (this.cartIdentifier !== 'cart-drawer') return;

        this.clearThirdPartyCartAppRefreshQueue();

        // Single delayed reinit. Multiple firings cause third-party apps
        // (Essential Upsell / Trust Badges) to re-bind handlers on their
        // existing buttons → one click registers as N cart adds.
        const timerId = window.setTimeout(() => {
            this.notifyThirdPartyCartApps(detail);
        }, 400);
        this._thirdPartyRefreshTimers.push(timerId);
    }

    _getCartItemCount(data) {
        const cartSection = data.sections?.[this.cartIdentifier];

        if (!cartSection) {
            console.warn(
                `Cart Drawer: section "${this.cartIdentifier}" missing in AJAX response. Returning 0 items.`
            );
            return 0;
        }

        const parseCartSectionHTML = new DOMParser().parseFromString(
            cartSection,
            'text/html'
        );

        const cartItemEl = parseCartSectionHTML.querySelector(this.tagName);
        if (!cartItemEl) return 0;

        // Parse count and handle Fokkio free products
        let count = Number(cartItemEl.dataset.cartitemcount || 0);

        // If free items exist in the drawer
        const freeItems = parseCartSectionHTML.querySelectorAll('.fokkio-free-item');
        freeItems.forEach((el) => {
            const quantity = Number(el.dataset.quantity || 0);
            if (quantity < 5) {
                // Treat as 0 in count
                count -= quantity;
            }
        });

        return count;
    }


    //new cart render Johannes

    renderCart(e) {
    const detail = e?.detail || {};
    const newCount = this._getCartItemCount(detail);
    const prevCount =
        typeof this._lastCartItemCount === 'number'
            ? this._lastCartItemCount
            : Number(this.dataset.cartitemcount || 0);

    // Render HTML + update dependent elements (this will also set dataset.cartitemcount)
    this.renderCartContents(detail);
    this.renderElementsBasedOnCartItems(detail);

    // Update tracker after render
    this._lastCartItemCount = newCount;

    // Only open the drawer when a new item was added (count increased)
    const shouldOpen = newCount > prevCount;

    if (
        shouldOpen &&
        this.cartIdentifier === 'cart-drawer' &&
        !this.closest('cart-drawer-component')?.isDrawerOpen
    ) {
        HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, this, {
            drawerType: 'cart-drawer',
        });
    }
}



    /* old rendercart commented by JOHANNES
    renderCart(e) {
        this.renderCartContents(e.detail);
        // Update dependent Elements to be render
        this.renderElementsBasedOnCartItems(e.detail);
        if (
            this.cartIdentifier == 'cart-drawer' &&
            !this.closest('cart-drawer-component').isDrawerOpen
        ) {
            HELPER_UTIL.dispatchCustomEvent(_EVENT_HELPER.showDrawer, this, {
                drawerType: 'cart-drawer',
            });
        }
    }

    */




    _get(property) {
        return this[property];
    }

    _set(property, value) {
        this[property] = value;
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerToastMessage,
            document,
            {
                toastType: 'common-errors',
                messageType: messageType,
                message: responseObj.message,
            }
        );
    }

    quantityElementAction({ line, quantity }) {
        const enable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'false')
            );
        };

        const disable = () => {
            this.querySelectorAll('quantity-selector').forEach((el) =>
                el.setAttribute('is-disabled', 'true')
            );
        };

        const resetToPreviousValue = () => {
            this.querySelector(`[item-index="${line}"]`)
                .querySelectorAll('quantity-selector input')
                .forEach((el) => {
                    el.value = (Number(quantity) - 1).toString();
                });
        };

        return {
            enable,
            disable,
            resetToPreviousValue,
        };
    }

    updateCartTotal(data) {
        const cartTotal = data.total_price;
        const cartTotalEl = document.querySelectorAll('[cart-total]');
        [...cartTotalEl].forEach((el) => {
            el.textContent = Shopify.formatMoney(
                cartTotal,
                Shopify.money_format
            );
        });
    }

    cartCheckoutEl() {
        // const checkoutContainer = document.querySelector(
        //     '[cart-checkout-container]'
        // );
        const hideContainerClass = 'hide';
        const show = () => {
            if (this.checkoutContainer.classList.contains(hideContainerClass)) {
                this.checkoutContainer.classList.remove(hideContainerClass);
            }
        };
        const hide = () => {
            this.checkoutContainer.classList.add(hideContainerClass);
        };

        return {
            show,
            hide,
        };
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        this.dataset.cartitemcount = cartItemCount;
        if (cartItemCount > 0) {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.showCartRecommendations,
                this
            );
        } else {
            HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.hideCartRecommendations,
                this
            );
        }
    }

    handleLineItem({ detail }) {
        // Make Api request to update cart drawer based on line item index
        /* Detail Object return data inform of @object as follows:
            {
            line: line Item index,
            quantity: Line item quantity
         } */
        const data = {
            id: detail.key,
            quantity: detail.quantity,
        };

        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            this.cartIdentifier
        ).getSectionsString();
        // Add sections key and strings of section id to be rendered
        // data['sections'] = HELPER_UTIL.sectionRenderUtil(
        //     this.sectionToRenderObj
        // ).getSectionsString();

        // Disable quantity selector of which quantity was increased
        this.quantityElementAction(detail).disable();

        HELPER_UTIL.fetchData({
            method: 'POST',
            url: `${window.Shopify.routes.root}cart/change.js?sections=${sectionsToRender}`,
            data: JSON.stringify(data),
            config: {
                headers: {
                    'Content-Type': 'application/json',
                },
            },
            onsuccess: (data) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    this,
                    { sections: data.sections }
                );

                HELPER_UTIL.fetchData({
                    method: 'GET',
                    url: `${window.Shopify.routes.root}cart/update.js?fokkio_request=true&sections=${sectionsToRender}`,
                    onsuccess: (updateData) => {
                        HELPER_UTIL.dispatchCustomEvent(
                            _EVENT_HELPER.triggerCartChange,
                            this,
                            { sections: updateData.sections }
                        );

                        // Final guard: pull a fresh /cart.js so the displayed
                        // quantity always matches the canonical cart state,
                        // even when third-party apps mutated the cart between
                        // our requests.
                        if (typeof window.__themeCartDrawerSync === 'function') {
                            window.__themeCartDrawerSync();
                        }
                    },
                });
            },
            onerror: (error) => {
                if (error.json) {
                    error.json().then((errorObj) => {
                        if (errorObj.status === 422) {
                            HELPER_UTIL.dispatchCustomEvent(
                                _EVENT_HELPER.triggerlineItemError,
                                this,
                                {
                                    errorObj: {
                                        lineItem: detail.key,
                                        responseObj: errorObj,
                                        messageType: 'error',
                                    },
                                }
                            );
                        }
                    });
                } else {
                    this.showDrawerToastMessage({
                        responseObj: {
                            message: 'Something went wrong.Please try again',
                        },
                        messageType: 'error',
                    });
                }
            },
            defaultCb: () => {
                this.quantityElementAction(detail).enable();
            },
        });
    }

    getHTMLFromString(dataToParse, elementToGet) {
        return new DOMParser()
            .parseFromString(dataToParse, 'text/html')
            .querySelector(elementToGet);
    }

    renderCartContents({ sections }) {
        try {
            const getSectionsToLoop = HELPER_UTIL.sectionRenderUtil(
                this.cartIdentifier
            ).getSectionsObject();

            if (!sections || typeof sections !== "object") {
                return;
            }

            let anySectionReplaced = false;

            getSectionsToLoop.forEach((sectionsObjItem) => {
                // Here sections{@object} is the data from the AJax ca;l we made to cart
                // sectionsObjItem @{object} is the class property consisting list of sections and there ids

                const safeSections = sections || {};
                const sectionHTML = safeSections[sectionsObjItem.sectionId];

                if (!sectionHTML) {
                    return; // ← prevent crash
                }

                const parsed = this.getHTMLFromString(
                    sectionHTML,
                    sectionsObjItem.queryElement
                );

                if (!parsed) {
                    return;
                }

                const liveTargets = document.querySelectorAll(sectionsObjItem.queryElement);
                const nextInner = parsed.innerHTML;
                [...liveTargets].forEach((section) => {
                    // Idempotency guard: avoid replacing innerHTML when the
                    // markup is byte-identical. Cheap string compare prevents
                    // a cascade where the post-render Essential Upsell init
                    // would otherwise visibly flicker the upsell cards on
                    // every no-op refresh.
                    if (section.innerHTML === nextInner) return;
                    section.innerHTML = nextInner;
                    anySectionReplaced = true;
                });
            });

            // Only kick the third-party app refresh (Essential Upsell init,
            // banners re-injection) when something actually changed. Without
            // this guard, every no-op render would still call init() and
            // dispatch cart:updated, fueling the cascade described above.
            if (!anySectionReplaced) return;

            // Mark render time so the third-party cart sync interceptor can
            // skip its debounced sync when the theme has just rendered.
            window.__lastDrawerRenderAt = Date.now();

            this.queueThirdPartyCartAppsRefresh({
                reason: 'cart-rendered',
            });
        } catch (error) {
            console.log(error);
        }
    }

    fetchAndRenderCartItems() {
        // Get liquid section  to render
        const sectionsToRender = HELPER_UTIL.sectionRenderUtil(
            this.cartIdentifier
        ).getSectionsString();
        // Make Ajax call to update.js and render cart
        HELPER_UTIL.fetchData({
            method: 'GET',
            url: `${window.Shopify.routes.root}cart/update.js?sections=${sectionsToRender}`,
            onsuccess: (data) => {
                HELPER_UTIL.dispatchCustomEvent(
                    _EVENT_HELPER.triggerCartChange,
                    this,
                    { sections: data.sections }
                );
            },
            onerror: (error) => {
                console.log(error);
            },
        });
    }

    disconnectedCallback() {
        this.clearThirdPartyCartAppRefreshQueue();
        // Remove all the event listeners added inside constructor to prevent memory leaks
        this.removeEventListener(
            _EVENT_HELPER.OnLineItemUpdate,
            this.handleLineItemBound
        );
        document.removeEventListener(
            _EVENT_HELPER.triggerCartChange,
            this.renderCartBound
        );
        document.removeEventListener(
            _EVENT_HELPER.updateAndShowCart,
            this.fetchAndRenderCartItemsBound
        );
        document.removeEventListener(
            _EVENT_HELPER.showDrawer,
            this.handleCartDrawerShownBound
        );
    }
}

customElements.get('drawer-cart') ||
    customElements.define('drawer-cart', DrawerCart);

class CartPage extends DrawerCart {
    constructor() {
        super();
    }

    renderElementsBasedOnCartItems(data) {
        const cartItemCount = this._getCartItemCount(data);
        if (cartItemCount > 0) {
            // this.updateCartTotal(data);
            if (this.checkoutContainer) {
                this.cartCheckoutEl().show();
            }
        } else {
            if (this.checkoutContainer) {
                this.cartCheckoutEl().hide();
            }
        }
    }

    showDrawerToastMessage({ responseObj, messageType }) {
        const toastEl = this.querySelector('toast-message');
        toastEl.toast({
            message: responseObj.message,
            messageType: messageType,
        });
    }
}

customElements.get('cart-page') || customElements.define('cart-page', CartPage);

class DrawerItem extends HTMLElement {
    constructor() {
        super();
        this.itemKey = this.getAttribute('item-key'); // Unique line item key of the product in cart
        this.removeItemElement = this.querySelectorAll('[remove-lineItem]');
        [...this.removeItemElement].forEach((el) => {
            el.addEventListener(
                'click',
                this.dispatchLineItemUpdate.bind(this)
            );

            el.addEventListener('keyup', (e) => {
                if (e.key === 'Enter') {
                    this.dispatchLineItemUpdate();
                }
            });
        });
        this.addEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.dispatchLineItemUpdate.bind(this)
        );

        document.addEventListener(
            _EVENT_HELPER.triggerlineItemError,
            this.showLineItemError.bind(this)
        );
    }

    dispatchLineItemUpdate(e) {
        const productQuantity = e?.detail.quantity;
        let detail = {
            key: this.itemKey,
            quantity: productQuantity ? productQuantity : '0',
        };
        HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.OnLineItemUpdate,
            this,
            detail
        );
    }

    handleLineItemErrorUpdate(errorObj) {
        let customMessageComponent =
            this.querySelector('custom-message') ||
            this.nextElementSibling.querySelector('custom-message');
        console.log(errorObj);
        // Adding  a
        // setTimeout(() => {
        customMessageComponent.showCustomMessage({
            errorMessage: errorObj.responseObj.message,
            messageType: errorObj.messageType,
        });
        // }, 0);
    }

    resetQuantitySelector() {
        this.querySelector('quantity-selector input').value =
            this.getAttribute('item-quantity');
    }

    showLineItemError(event) {
        const { errorObj } = event.detail;
        if (errorObj && errorObj.lineItem === this.itemKey) {
            this.handleLineItemErrorUpdate(errorObj);
            this.resetQuantitySelector();
        }
    }

    disconnectedCallback() {
        // Remove all the event listeners added inside constructor to prevent memory leaks
        this.removeEventListener(
            _EVENT_HELPER.onQuantityChange,
            this.dispatchLineItemUpdate.bind(this)
        );

        [...this.removeItemElement].forEach((el) => {
            el.removeEventListener(
                'click',
                this.dispatchLineItemUpdate.bind(this)
            );
            el.removeEventListener('keyup', (e) => {
                if (e.key === 'Enter') {
                    this.dispatchLineItemUpdate();
                }
            });
        });
    }
}

customElements.get('drawer-item') ||
    customElements.define('drawer-item', DrawerItem);
